import { PROJECT_ONE_QUERY } from "@/gql/project";
import { SELLER_UNITS_AND_PROJECTS_QUERY } from "@/gql/sup";
import { amenitiesInfo } from "@/info/amenities";
import { Project } from "@/types/project";
import { type NearbyProperty, type SellerUnitsAndProjectsOne } from "@/types/sup";
import { getApolloData } from "@/utils/apollo/get-data";
import { logger } from "@/utils/logger";
import { removeTypename } from "@/utils/removeTypeName";
import { toSentenceCase } from "@/utils/string/to-sentence-case";
import { cache } from "react";
import { getNearbySUPs } from "./get-nearby-sups";
import { s3Url } from "./s3-prefix";

export interface ProjectPageData {
	id: string;
	name: string;
	location: string;
	images: {
		url: string;
		space: string;
	}[];
	additionalFeatures?: string[];
	projectInfo: {
		locality: string;
		age: string;
		blocks: number;
		maxFloors: number;
		units: number;
		waterSource: string;
		approvals: string;
		bankApprovals: string;
		documents: string;
		additionalFeatures?: string[];
		name?: string;
	};
	amenities: {
		name: string;
		icon?: string;
		code: string;
	}[];
	availableSUPs: SellerUnitsAndProjectsOne[];
	gps?: {
		lat: number;
		lng: number;
	};
	linkedLocalities?: string[];
	slug?: string;
	nearByProjects?: NearbyProperty[];
	ageStatus?: {
		label: string;
		value: string;
	};
	propertyType?: string[];
	specifications?: string;
	area?: string;
	roads?: string[];
	rera?: {
		id: string;
	};
	createdAt?: Date;
	updatedAt?: Date;
}

export const getProjectData = async (id: string): Promise<ProjectPageData | { error: string }> => {
	try {
		logger.info(`Fetching project ${id}`);
		const { data, error: error1 } = await getApolloData<{
			projects: {
				one: Project;
			};
		}>({
			query: PROJECT_ONE_QUERY,
			variables: {
				filters: {
					one: {
						projectId: id,
					},
				},
			},
			fetchPolicy: "cache-first",
		});
		// Fetch available SUPs for this project
		const { data: supData, error: error2 } = await getApolloData<{
			sellerUnitsAndProjects: {
				details: SellerUnitsAndProjectsOne[];
			};
		}>({
			query: SELLER_UNITS_AND_PROJECTS_QUERY,
			variables: {
				params: {
					details: {
						projectIds: [id],
						saleStatus: "onSale",
					},
				},
			},
			fetchPolicy: "cache-first",
		});

		if (error1 || error2) {
			logger.error("Failed to fetch project", {
				method: "getProjectData",
				query: error1
					? "PROJECT_ONE_QUERY (projects.one)"
					: "SELLER_UNITS_AND_PROJECTS_QUERY (sellerUnitsAndProjects.details)",
				id,
				error: error1 || error2,
			});
			if (error1) return { error: "Error fetching project" };
		}

		if (!data) {
			return { error: "Error fetching project" };
		}

		const project = removeTypename((data.projects ?? {}).one ?? {}) as Project;
		const availableSUPs = removeTypename(supData?.sellerUnitsAndProjects?.details ?? []) as SellerUnitsAndProjectsOne[];

		const projectData = {
			...project,
			id: project.id,
			name: project.name,
			propertyType: (project.propertyTypes || []).filter(Boolean),
			location: toSentenceCase(project.location?.locality || ""),
			images:
				project.images?.map(image => ({
					url: s3Url(image.original),
					space: image.tags?.length ? image.tags[0] : image.space,
				})) || [],
			projectInfo: {
				locality: toSentenceCase(project.location?.locality || ""),
				age: project.ageStatus?.value || "",
				blocks: project.blocks?.number || 0,
				maxFloors: project.blocks?.maxFloors || 0,
				units: project.blocks?.numOfUnits || 0,
				waterSource: project.waterSource,
				approvals: project.approvalsStr,
				bankApprovals: project.bankApprovalsStr,
				documents: project.documentsStr,
				additionalFeatures: project.additionalFeatures,
				name: project.name,
			},
			amenities: project.amenitiesStr
				? project.amenitiesStr.split(", ").map(amenity => ({
						name: amenity,
						icon: amenitiesInfo.find(a => a.name === amenity)?.icon,
						code: amenitiesInfo.find(a => a.name === amenity)?.code || amenity,
					}))
				: [],
			availableSUPs,
			gps: project.location?.gps?.coordinates
				? {
						lat: project.location.gps.coordinates[1],
						lng: project.location.gps.coordinates[0],
					}
				: undefined,
			roads: project.location?.roads,
		};

		// Fetch nearby projects if coordinates are available
		let nearByProjects: NearbyProperty[] = [];
		if (projectData.gps) {
			try {
				const np = await getNearbySUPs({
					coordinates: { lat: projectData.gps.lat, lng: projectData.gps.lng },
					type: "sp",
					excludeId: id,
				});
				nearByProjects = (np?.data ? removeTypename(np.data) : []) as unknown as NearbyProperty[];
			} catch (error) {
				logger.error("Failed to fetch nearby projects", {
					method: "getProjectData.getNearbySUPs",
					query: "getNearbySUPs",
					id,
					error,
				});
				// Continue without nearby projects data
			}
		}

		return {
			...projectData,
			nearByProjects,
		};
	} catch (error) {
		logger.error("Failed to fetch project", {
			method: "getProjectData",
			query: "PROJECT_ONE_QUERY or SELLER_UNITS_AND_PROJECTS_QUERY",
			id,
			error,
		});
		return { error: "Error fetching project" };
	}
};

export const getProjectDataCached = cache(getProjectData);
